import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axiosInstance';

export const createEmployee = createAsyncThunk('employee/addEmployee', async (employeeData, { rejectWithValue }) => {
  try {
  const formData = new FormData();
  for (const key in employeeData) {
    formData.append(key, employeeData[key]);
  }
  const response = await axiosInstance.post('/employee', formData);
  return response.data;
  } catch (err) {
    if (err.response && err.response.data && err.response.data.message) {
      return rejectWithValue(err.response.data.message);
    }

    // Fallback to default error message
    return rejectWithValue(err.message || 'An unexpected error occurred');
  }
});

export const fetchEmployees = createAsyncThunk('employee/fetchEmployees', async () => {
  const response = await axiosInstance.get('/employee');
  return response.data;
});

export const deleteEmployee = createAsyncThunk('employee/deleteEmployee', async (id) => {
  const response = await axiosInstance.delete(`/employee/${id}`);
  return response.data;
});

export const fetchEmployee = createAsyncThunk('employee/fetchEmployee', async (id) => {
  const response = await axiosInstance.get(`/employee/${id}`);
  return response.data;
});

export const updateEmployee = createAsyncThunk('employee/updateEmployee', async (employeeData) => {
  const response = await axiosInstance.put(`/employee/${employeeData.id}`, employeeData.formData);
  return response.data;
});

const employeeSlice = createSlice({
  name: 'employee',
  initialState: {
    employees: [],
    status: 'idle',
    error: null,
  },
  reducers: {
    clearEmployees: (state) => {
      state.employees = [];
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createEmployee.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employees.push(action.payload);
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchEmployees.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employees = action.payload;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteEmployee.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employees = action.payload;
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchEmployee.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEmployee.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(fetchEmployee.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateEmployee.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Listen for store changes and clear employees
      .addMatcher(
        (action) => action.type === 'store/setSelectedStore' || action.type === 'store/clearStoreData',
        (state, action) => {
          // Clear employees when store changes
          state.employees = [];
          state.status = 'idle';
          state.error = null;
        }
      );
  },
});

export const { clearEmployees } = employeeSlice.actions;
export default employeeSlice.reducer;
